import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { FormControl, FormGroup } from '@angular/forms';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { BasicService } from '../../shared/services/basic.service';
import {
  CustomField,
  CustomFieldsForTaskProcessPage,
  CustomFieldTypeName,
  CustomFieldValue,
} from '../custom-fields.model';
import { CustomFieldsService } from '../custom-fields.service';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';

@Component({
  selector: 'app-task-process-custom-fields',
  templateUrl: './task-process-custom-fields.component.html',
  styleUrls: ['./task-process-custom-fields.component.scss'],
})
export class TaskProcessCustomFieldsComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;

  taskProcessPageFields: CustomFieldsForTaskProcessPage;

  userFieldsFormGroup: FormGroup;
  conversationFieldsFormGroup: FormGroup;
  isUserReadOnly: boolean;

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private customFieldsService: CustomFieldsService
  ) {}

  ngOnInit() {
    if (!this.taskProcessContext.processedTaskInfo || this.taskProcessContext.engagement) {
      return;
    }
    this.update();
    this.subscriptions.add(this.taskProcessContext.crmIFrameDidClose$.subscribe(() => this.update()));
    this.isUserReadOnly = this.alcmeonContextService.isLoggedUserReadOnlyForSite(this.taskProcessContext.site.id);
  }

  private update() {
    const customFieldContext = this.taskProcessContext.processedTaskInfo.customFieldContext;
    this.customFieldsService.getFieldsForTaskProcessPage(customFieldContext).subscribe(fields => {
      this.taskProcessPageFields = fields;
      this.userFieldsFormGroup = this.buildFormGroup(fields.user);
      this.conversationFieldsFormGroup = this.buildFormGroup(fields.conversation);
    });
  }

  getUserFieldFormGroup(field): FormGroup {
    return this.userFieldsFormGroup.get(this.fieldFormGroupName(field)) as FormGroup;
  }

  getConversationFieldFormGroup(field): FormGroup {
    return this.conversationFieldsFormGroup.get(this.fieldFormGroupName(field)) as FormGroup;
  }

  handleValidationError(formGroup: FormGroup, error: any) {
    if (this.customFieldsService.hasValidationErrors(error)) {
      for (const validationError of error.error.details) {
        formGroup.get(String(validationError.field_id)).setErrors({ validationError });
      }
    }
  }

  saveUserFields() {
    const valuesById = this.buildFieldValuesById(this.userFieldsFormGroup, this.taskProcessPageFields.user);
    this.customFieldsService.updateUserFieldValues(this.getUserId(), valuesById).subscribe(
      () => {
        this.userFieldsFormGroup.markAsPristine();
        this.basicService.toastSuccess('m/custom-field/save-field-value/success');
      },
      error => {
        this.handleValidationError(this.userFieldsFormGroup, error);
      }
    );
  }

  saveConversationFields() {
    const valuesById = this.buildFieldValuesById(
      this.conversationFieldsFormGroup,
      this.taskProcessPageFields.conversation
    );
    this.customFieldsService.updateConversationFieldValues(this.getConversationId(), valuesById).subscribe(
      () => {
        this.conversationFieldsFormGroup.markAsPristine();
        this.basicService.toastSuccess('m/custom-field/save-field-value/success');
      },
      error => {
        this.handleValidationError(this.conversationFieldsFormGroup, error);
      }
    );
  }

  // builds payload for /PUT endpoint
  private buildFieldValuesById(formGroup: FormGroup, fields: CustomField[]): { [id: string]: CustomFieldValue } {
    const valuesByFieldId = {};
    for (const field of fields) {
      const id = this.fieldFormGroupName(field);
      const fieldValue = this.customFieldsService.convertFieldValueForBackend(
        formGroup.get(id).get('fieldValue').value,
        field
      );
      if (field.isEditable) {
        valuesByFieldId[id] = fieldValue;
      }
    }
    return valuesByFieldId;
  }

  private fieldFormGroupName(field) {
    return String(field.definition.id);
  }

  private buildFormGroup(fields: CustomField[]) {
    const subGroupsByFieldId = {};
    for (const field of fields) {
      const groupName = this.fieldFormGroupName(field);
      let fieldValue = field.value;
      if (fieldValue && field.definition.type.type === CustomFieldTypeName.Phone) {
        fieldValue = this.customFieldsService.buildPhoneFieldValueForFormControl(fieldValue);
      }
      subGroupsByFieldId[groupName] = new FormGroup({
        fieldValue: new FormControl({ value: fieldValue, disabled: !field.isEditable || this.isUserReadOnly }),
      });
    }
    return new FormGroup(subGroupsByFieldId);
  }

  private getUserId() {
    return this.taskProcessContext.processedTaskInfo.customFieldContext.customFieldUserId;
  }

  private getConversationId() {
    return this.taskProcessContext.processedTaskInfo.customFieldContext.customFieldConversationId;
  }

  ngOnDestroy() {
    this.subscriptions?.unsubscribe();
  }
}
